import React, { Fragment, useState, useEffect, useContext } from 'react';
import { FiArrowLeft } from 'react-icons/fi';
import { MdLocalPhone, MdTablet } from 'react-icons/md';
import { AiFillClockCircle } from 'react-icons/ai';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Scrollbar from 'perfect-scrollbar';
import isEmpty from 'lodash/isEmpty';
import { isMobile } from 'react-device-detect';
import { GlobalContext, getMarinaTextId } from 'context/GlobalProvider';
import { AuthContext } from 'context/AuthProvider';
import Loader from 'components/Loader/Loader';
import CardCarousel from 'components/CardCarousel/CardCarousel';
import Row from 'components/UI/Antd/Grid/Row';
import Col from 'components/UI/Antd/Grid/Col';
import ImageWithFallback from 'components/UI/ImageWithFallback/ImageWithFallback';
import VesselSelect from 'components/VesselSelect/VesselSelect';
import useWindowSize from 'library/hooks/useWindowSize';
import Container from 'components/UI/Container/Container.style';
import Reservation from '../Reservation/Reservation';
import Messages from './Messages';
import { MARINA_PAGE, MESSAGES_PAGE, NOT_FOUND_PAGE } from 'settings/constant';
import associations from 'settings/associations';
import { setStateToUrl } from 'library/helpers/url_handler';
import {
  MessagingWrapper,
  MarinaTitleWrapper,
  MarinaTitle,
  Sidebar,
  SidebarScrollInner,
  SidebarHeader,
  MarinaDetails,
  InfoItem,
  InfoLabel,
  Info,
  AssociationInfo,
  AssociationTitle,
  AssociationCard,
  AssociationName
} from './MessagingPage.style';

const MessagingPage = ({ match, history, location, promotions }) => {
  const { t } = useTranslation();
  const { width } = useWindowSize();
  const { loading, getSingleMarina, marinas } = useContext(GlobalContext);
  const { user, selectedBoaterVessel, selectBoaterVessel } =
    useContext(AuthContext);

  const [marina, setMarina] = useState({});

  const vesselsWithHomeMarina = user && user.vessels && user.vessels.length
    ? user.vessels.filter(v => v.home_marina_id)
    : [];

  const { images, phone, country_code, hours, url, association } = marina || {};

  const isHomeMarina = vesselsWithHomeMarina.some(
    ({ home_marina_id }) => home_marina_id === marina.id
  );

  useEffect(() => {
    if (!loading) {
      const currentMarina = getSingleMarina(match.params.id);
      setMarina(currentMarina);
      if (!currentMarina) {
        history.push(`/${match.params.locale}${NOT_FOUND_PAGE}`);
      }
    }
  }, [loading, match.params.id]);

  useEffect(() => {
    let ps;
    if (!isEmpty(marina) && width > 767 && !isMobile) {
      const cont = document.getElementById('asideScrollContainer');
      ps = new Scrollbar(cont);
    }

    return () => {
      ps && ps.destroy();
      ps = null;
    };
  }, [width, marina]);

  useEffect(() => {
    if (selectedBoaterVessel && isHomeMarina) {
      const selectedVesselWithHomeMarina = vesselsWithHomeMarina.filter(
        ({ id }) => id === selectedBoaterVessel
      );
      if (selectedVesselWithHomeMarina.length) {
        setTimeout(() => {
          // change the marina to the home marina of the selected vessel
          const textId = getMarinaTextId(
            marinas.find(
              ({ id }) => id === selectedVesselWithHomeMarina[0].home_marina_id
            )
          );
          history.replace(
            `/${match.params.locale}${MARINA_PAGE}/${textId}${MESSAGES_PAGE}`
          );
        }, 300);
      }
    }
  }, [selectedBoaterVessel, isHomeMarina]);

  useEffect(() => {
    if (!isEmpty(marina) && (width < 768 || isMobile)) {
      setTimeout(() => {
        const textId = getMarinaTextId(marina);
        history.replace(
          `/${match.params.locale}${MARINA_PAGE}/${textId}${setStateToUrl({
            open_messages: true
          })}`
        );
      }, 300);
    }
  }, [selectedBoaterVessel, isHomeMarina]);

  useEffect(() => {
    if (!isEmpty(marina) && (width < 768 || isMobile)) {
      setTimeout(() => {
        const textId = getMarinaTextId(marina);
        history.replace(
          `/${match.params.locale}${MARINA_PAGE}/${textId}${setStateToUrl({
            open_messages: true
          })}`
        );
      }, 300);
    }
  }, [marina, width]);

  if (loading || isEmpty(marina) || width < 768 || isMobile) {
    return <Loader />;
  }

  return (
    <MessagingWrapper>
      <Container fullWidth={true}>
        <Row gutter={30}>
          <Col md={9}>
            <Sidebar>
              <SidebarScrollInner id='asideScrollContainer'>
                {isHomeMarina && <VesselSelect onlyHMRVessels={true} />}
                <SidebarHeader>
                  {images && images.length && (
                    <CardCarousel height={134}>
                      {images.slice(0, 5).map(({ thumb }, index) => (
                        <ImageWithFallback
                          key={index}
                          draggable={false}
                          src={thumb}
                          alt={`${marina.name} pic ${index + 1}`}
                        />
                      ))}
                    </CardCarousel>
                  )}
                  <MarinaTitleWrapper>
                    <Link
                      className='link'
                      to={{
                        pathname: `/${match.params.locale}${MARINA_PAGE}/${match.params.id}`,
                        state: {
                          ref: 'message_to_marina'
                        }
                      }}
                    >
                      <FiArrowLeft />
                      <MarinaTitle>{marina.name}</MarinaTitle>
                    </Link>
                  </MarinaTitleWrapper>
                </SidebarHeader>
                {!isHomeMarina ? (
                  <Reservation
                    marina={marina}
                    isMessaging={true}
                    location={location}
                    history={history}
                    promotions={promotions}
                  />
                ) : (
                  <MarinaDetails>
                    {!!phone && (
                      <InfoItem>
                        <InfoLabel>
                          <MdLocalPhone size={24} />
                          <span>{t('PHONE')}</span>
                        </InfoLabel>
                        <Info>{!isEmpty(country_code) ? country_code + phone : phone}</Info>
                      </InfoItem>
                    )}
                    {!!hours && (
                      <InfoItem>
                        <InfoLabel>
                          <AiFillClockCircle size={22} />
                          <span>{t('OFFICE_HOURS')}</span>
                        </InfoLabel>
                        <Info>{hours}</Info>
                      </InfoItem>
                    )}
                    {!!url && (
                      <InfoItem>
                        <InfoLabel>
                          <MdTablet size={24} />
                          <span>{t('WEBSITE')}</span>
                        </InfoLabel>
                        <Info>
                          <a href={url} target='_blank'>
                            {url}
                          </a>
                        </Info>
                      </InfoItem>
                    )}
                    {!isEmpty(associations) && associations[association.code] && (
                      <AssociationInfo>
                        <AssociationTitle>{t('MEMBER_OF')}</AssociationTitle>
                        <AssociationCard>
                          <img
                            src={associations[association.code].logo}
                            alt='Membership card'
                          />
                        </AssociationCard>
                        <AssociationName>
                          {associations[association.code].name}
                        </AssociationName>
                      </AssociationInfo>
                    )}
                  </MarinaDetails>
                )}
              </SidebarScrollInner>
            </Sidebar>
          </Col>
          <Col md={15}>
            <Messages marina={marina} />
          </Col>
        </Row>
      </Container>
    </MessagingWrapper>
  );
};

export default MessagingPage;
